'use client'

import { useEffect } from 'react'
import { useSearchParams, usePathname } from 'next/navigation'
import { getCookie } from 'cookies-next'
import { ATTRIBUTION_PARAMS, IN_APP_BROWSER_COOKIE } from '@/hooks/useAttributionTracking'
import { trackEvent } from '@nile/mantine-theme/analytics'
import { detectInAppBrowser } from '@/lib/detectInAppBrowser'

const SESSION_STORAGE_KEY = 'ad_landing_tracked'

/**
 * Fires a Segment event when a user lands on any page from a paid ad.
 * Criteria: URL must contain a click ID (fbclid, gclid, msclkid, or oppref)
 *
 * Use case: Debug attribution tracking by comparing Segment events to campaign_attributions DB rows.
 * Can be added to any layout - fires once per session when ad click ID detected.
 */
export default function AdLandingTracker() {
    const searchParams = useSearchParams()
    const pathname = usePathname()

    useEffect(() => {
        if (sessionStorage.getItem(SESSION_STORAGE_KEY)) return

        const hasAdClickId =
            searchParams.get('fbclid') ||
            searchParams.get('gclid') ||
            searchParams.get('msclkid') ||
            searchParams.get('oppref')

        // Campaign landing pages (e.g. LillyDirect) arrive with UTMs but no click ID.
        const isCampaignLanding = pathname.startsWith('/p/')
        const hasAttributionParams =
            isCampaignLanding && ATTRIBUTION_PARAMS.some((param) => searchParams.get(param))

        if (!hasAdClickId && !hasAttributionParams) return

        const urlParams: Record<string, string | null> = {}
        ATTRIBUTION_PARAMS.forEach((param) => {
            urlParams[param] = searchParams.get(param)
        })

        const cookieParams: Record<string, string> = {}
        ATTRIBUTION_PARAMS.forEach((param) => {
            const value = getCookie(param)
            if (value) cookieParams[param] = String(value)
        })

        const inAppBrowser = String(getCookie(IN_APP_BROWSER_COOKIE) || '') || detectInAppBrowser() || ''

        trackEvent('ad_landing_page_view', {
            // URL params (what we received)
            url_utm_source: urlParams.utm_source,
            url_utm_medium: urlParams.utm_medium,
            url_utm_campaign: urlParams.utm_campaign,
            url_utm_content: urlParams.utm_content,
            url_gclid: urlParams.gclid,
            url_gbraid: urlParams.gbraid,
            url_wbraid: urlParams.wbraid,
            url_fbclid: urlParams.fbclid,
            url_msclkid: urlParams.msclkid,
            url_kad_cid: urlParams.kad_cid,
            url_oppref: urlParams.oppref,
            // Cookie params (what got stored)
            cookie_utm_source: cookieParams.utm_source,
            cookie_utm_medium: cookieParams.utm_medium,
            cookie_utm_campaign: cookieParams.utm_campaign,
            cookie_utm_content: cookieParams.utm_content,
            cookie_gclid: cookieParams.gclid,
            cookie_gbraid: cookieParams.gbraid,
            cookie_wbraid: cookieParams.wbraid,
            cookie_fbclid: cookieParams.fbclid,
            cookie_msclkid: cookieParams.msclkid,
            cookie_kad_cid: cookieParams.kad_cid,
            cookie_oppref: cookieParams.oppref,
            in_app_browser: inAppBrowser,
            full_url: typeof window !== 'undefined' ? window.location.href : '',
            landing_page: pathname,
            timestamp: new Date().toISOString(),
        })

        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    }, [searchParams, pathname])

    return null
}

